import { useEffect, useMemo, useState } from 'react';
import { members as Api, type EnrolOption, type EnrolOptions, type Member, type PayMethod } from '@tvx/api-client';
import { Modal } from '../components/Modal';

type Kind = 'pass' | 'course' | 'batch';
type Cat = 'news' | 'calls' | 'course' | 'batch';
const CATS: { key: Cat; label: string }[] = [
  { key: 'news', label: 'News' }, { key: 'calls', label: 'Trade Calls' },
  { key: 'course', label: 'Course' }, { key: 'batch', label: 'Batch' },
];
/** One thing they can be added to, tagged with what it is. */
type Choice = EnrolOption & { kind: Kind; group?: string };
const key = (c: Choice) => `${c.kind}:${c.id}`;

/**
 * Add a member to a pass, course or batch.
 *
 * With a payment it writes one paid line into Payments and the usual
 * grant-on-paid opens the access; without, only the access opens. The price
 * of the thing picked fills the amount, and can be changed.
 */
export function EnrolForm({ member, onCancel, onDone }: {
  member: Member;
  onCancel: () => void;
  onDone: (message: string) => void;
}) {
  const [opts, setOpts] = useState<EnrolOptions | null>(null);
  const [cat, setCat] = useState<Cat>('news');
  const [pick, setPick] = useState('');
  const [pay, setPay] = useState(true);
  const [method, setMethod] = useState<PayMethod>('UPI');
  const [amount, setAmount] = useState('');
  const [typed, setTyped] = useState(false);
  const [reference, setReference] = useState('');
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { void Api.enrolOptions().then(setOpts).catch((e: Error) => setError(e.message)); }, []);

  /* The category narrows the list. Course holds the full-library passes as
     well as single courses, since both are bought as "learning". */
  const list: Choice[] = useMemo(() => {
    if (!opts) return [];
    const passes = (m: string, group?: string): Choice[] =>
      opts.passes.filter((p) => p.module === m).map((p) => ({ ...p, kind: 'pass', group }));
    if (cat === 'news') return passes('news');
    if (cat === 'calls') return passes('calls');
    if (cat === 'batch') return opts.batches.map((b) => ({ ...b, kind: 'batch' }));
    return [...passes('learning', 'Full library'),
      ...opts.courses.map((c): Choice => ({ ...c, kind: 'course', group: 'Single course' }))];
  }, [opts, cat]);
  const chosen = list.find((o) => key(o) === pick) ?? list[0];
  const groups = [...new Set(list.map((o) => o.group ?? ''))];

  /* The price follows the pick until somebody types their own. */
  useEffect(() => { if (!typed) setAmount(chosen ? String(chosen.price || '') : ''); }, [chosen, typed]);

  const gst = Math.round((Number(amount) || 0) * 18 / 118);

  async function submit() {
    if (!chosen) { setError('Pick what to add them to.'); return; }
    if (pay && !(Number(amount) > 0)) { setError('An amount is required to record a payment.'); return; }
    setBusy(true); setError('');
    try {
      const r = await Api.enrol(member.id, {
        kind: chosen.kind, id: chosen.id,
        ...(pay ? { pay: { amount: Number(amount), method, reference, note, paid_at: paidAt || null } } : {}),
      });
      onDone(`${member.name || member.email} is now in ${r.label}${r.payment ? `. Payment ${r.payment.invoice_no} recorded.` : '.'}`);
    } catch (e) { setError((e as Error).message); setBusy(false); }
  }

  return (
    <Modal title={`Add to a program · ${member.name || member.email}`} onClose={onCancel} footer={
      <>
        <span className="mdl-spacer" />
        <button className="b bo" type="button" onClick={onCancel}>Cancel</button>
        <button className="b bg" type="button" disabled={busy || !chosen} onClick={() => void submit()}>
          {busy ? 'Adding…' : pay ? 'Add & record payment' : 'Add'}
        </button>
      </>
    }>
      {error ? <div className="err">{error}</div> : null}

      <div className="fld">
        <label className="lbl">1 · Category</label>
        <div className="sg" style={{ margin: 0 }}>
          {CATS.map((c) => (
            <span key={c.key} {...(cat === c.key ? { 'data-on': '' } : {})}
              onClick={() => { setCat(c.key); setPick(''); setTyped(false); }}>{c.label}</span>
          ))}
        </div>
      </div>

      <div className="fld">
        <label className="lbl">2 · {cat === 'batch' ? 'Batch' : cat === 'course' ? 'Course or library' : 'Plan'}</label>
        <select className="inp" value={chosen ? key(chosen) : ''} disabled={!list.length}
          onChange={(e) => { setPick(e.target.value); setTyped(false); }}>
          {groups.map((g) => {
            const items = list.filter((o) => (o.group ?? '') === g);
            const rows = items.map((o) => (
              <option key={key(o)} value={key(o)}>
                {o.label}{o.days ? ` · ${o.days} days` : ''}{o.price ? ` · ₹${o.price.toLocaleString('en-IN')}` : ''}
              </option>
            ));
            return g ? <optgroup key={g} label={g}>{rows}</optgroup> : rows;
          })}
        </select>
        {opts && !list.length ? <p className="hint" style={{ marginTop: 6 }}>Nothing in this category has been created yet.</p> : null}
      </div>

      <div className="crd" style={{ padding: '14px 16px', borderStyle: 'dashed', borderColor: 'var(--gold)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div className="kh">3 · Payment</div>
          <div className="sg" style={{ margin: 0 }}>
            <span {...(pay ? { 'data-on': '' } : {})} onClick={() => setPay(true)}>Record a payment</span>
            <span {...(!pay ? { 'data-on': '' } : {})} onClick={() => setPay(false)}>No payment (goodwill)</span>
          </div>
        </div>
        {pay ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginTop: 14 }}>
            <div className="fld"><label className="lbl">Method</label>
              <select className="inp" value={method} onChange={(e) => setMethod(e.target.value as PayMethod)}>
                {(opts?.payMethods ?? [method]).map((m) => <option key={m}>{m}</option>)}
              </select></div>
            <div className="fld"><label className="lbl">Amount (₹, incl. GST)</label>
              <input className="inp" type="number" min={0} value={amount}
                onChange={(e) => { setAmount(e.target.value); setTyped(true); }} /></div>
            <div className="fld"><label className="lbl">Reference</label>
              <input className="inp" value={reference} placeholder="UTR or transaction id"
                onChange={(e) => setReference(e.target.value)} /></div>
            <div className="fld"><label className="lbl">Paid on</label>
              <input className="inp" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} /></div>
            <div className="fld" style={{ gridColumn: 'span 2' }}><label className="lbl">Note</label>
              <input className="inp" value={note} onChange={(e) => setNote(e.target.value)} /></div>
          </div>
        ) : null}
      </div>
      <p className="hint" style={{ margin: 0 }}>
        {pay ? `Writes one paid line to Payments (incl. ₹${gst.toLocaleString('en-IN')} GST) and opens the access.`
          : 'Opens the access only. Nothing is added to Payments.'}
      </p>
    </Modal>
  );
}
